import { WarningIcon } from "@images/svgIcons/warningIcon"
import { BoxConsumableDetailsEntity } from "@modules/boxConsumable/entities/boxConsumableDetailsEntity"
import { BoxOfferType } from "@modules/boxOffer/entities/boxOfferTypes"
import { getBoxOfferIcon, getOfferCommitment } from "@modules/boxOffer/utils/boxOfferUtils"
import { BoxSubscriptionDetailsEntity } from "@modules/boxSubscription/entities/boxSubscriptionDetailsEntity"
import { BoxSubscriptionStatus } from "@modules/boxSubscription/gateway/boxSubscriptionApiDto"
import { CouponType } from "@modules/common/couponObject/gateway/couponObjectApiDto"
import { getFormattedCouponReduction } from "@modules/common/couponObject/utils/couponObjectUtils"
import { word } from "@modules/core/utils/i18n"
import { formatCurrency } from "@modules/currency/currency"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { HUText } from "@modules/ui/components/huText"
import { capitalize } from "@modules/utils/stringUtils"
import dayjs from "dayjs"
import { motion, useInView } from "framer-motion"
import { ChevronDown, ChevronUp, Dot, Percent, Tag } from "lucide-react"
import { useRef, useState } from "react"
import { useTheme } from "styled-components"

type Props = {
  item: BoxConsumableDetailsEntity | BoxSubscriptionDetailsEntity
  menuActions?: React.JSX.Element
  className?: string
} & React.ComponentProps<"div">

export function MemberOfferItem({ item, menuActions, className, ...props }: Props) {
  const ref = useRef<HTMLDivElement>(null)

  const theme = useTheme()
  const language = useLanguage()
  const isInView = useInView(ref, { initial: false })

  const defaultClassName = "card flex flex-column gap-4 mb-0 p-0 px-4 py-3 w-full"
  const [isCollapsed, setIsCollapsed] = useState(true)

  const toggleCollapse = () => {
    setIsCollapsed((prev) => !prev)
  }

  return (
    <div className={className ?? defaultClassName} key={item.id} {...props}>
      {/* HEADER */}
      <div className="flex flex-column gap-2 w-full">
        <div className="flex flex-row w-full justify-content-between align-items-center">
          <div className="flex flex-row align-items-center">
            <HUText
              fontStyle="BM"
              color={theme.mainWhite}
              style={{ whiteSpace: "break-spaces", overflowWrap: "break-word" }}
              className="max-w-10rem w-10rem"
            >
              {item.offer.name}
            </HUText>
            <Dot strokeWidth={10} color={item.getColorStatus(theme)} />
            <HUText
              fontStyle="LS"
              color={theme.neutralColor}
              className="max-w-5rem"
              style={{ whiteSpace: "break-spaces", overflowWrap: "break-word" }}
            >
              {item.getWordingStatus()}
            </HUText>
          </div>
          <div className="flex gap-2">
            {item instanceof BoxConsumableDetailsEntity && item.offer.type === BoxOfferType.TicketsBook && (
              <div className="flex flex-row gap-2 align-items-center border-1  border-neutralColor-200 rounded-full px-2 py-1">
                <HUText fontStyle="LL" color={theme.mainWhite}>
                  {item.count}/{item.offer.count}
                </HUText>
                <HUText fontStyle="BS" color={theme.neutralColor600}>
                  {word("box.member.table.offer.item.sessionsLeft")}
                </HUText>
              </div>
            )}
            {isCollapsed ? (
              <ChevronDown size={25} onClick={toggleCollapse} className="cursor-pointer" />
            ) : (
              <ChevronUp size={25} onClick={toggleCollapse} className="cursor-pointer" />
            )}
          </div>
        </div>
        {/* WARNING */}
        {item.isDisplayWarning() && (
          <div className="flex w-max align-items-center text-center gap-1 rounded-full bg-ruby-700 py-1 px-2">
            <WarningIcon color={theme.error300} width={14} height={14} />
            <HUText fontStyle="LS" color={theme.mainWhite}>
              {item.getWordingWarning()}
            </HUText>
          </div>
        )}
      </div>
      {/* BODY EXPANDABLE */}
      <motion.div
        animate={{ height: isCollapsed ? 0 : "auto" }}
        className={`flex-column gap-3 w-full ${isCollapsed ? "hidden" : "flex"} ${!isCollapsed && isInView ? "overflow-visible" : "overflow-hidden"}`}
      >
        {/* TYPE */}
        <div className="flex flex-column gap-2 w-full">
          <div className="flex w-full gap-2 align-items-baseline">
            <HUText fontStyle="BS" color={theme.neutralColor600}>
              {word("box.member.table.offer.item.type")}
            </HUText>
            <div className="flex flex-grow-1 border-top-1 border-neutralColor-200" />
          </div>
          <div className="flex w-full gap-2 align-items-center">
            {getBoxOfferIcon(item.offer.type)}
            <HUText fontStyle="LL" color={theme.mainWhite}>
              {word(`box.offer.form.types.${item.offer.type}`)}
            </HUText>
          </div>
        </div>
        {/* STATUS */}
        <div className="flex flex-column gap-2 w-full">
          <div className="flex w-full gap-2 align-items-baseline">
            <HUText fontStyle="BS" color={theme.neutralColor600}>
              {word("box.member.table.offer.item.status.label")}
            </HUText>
            <div className="flex flex-grow-1 border-top-1 border-neutralColor-200" />
          </div>
          <div className="flex flex-column w-full gap-1">
            <div className="flex align-items-center gap-1 pl-3">
              <HUText fontStyle="LL" color={theme.mainWhite}>
                {item.getWordingStatus()}
              </HUText>
              {item.status === BoxSubscriptionStatus.PAUSE && item.pause && (
                <>
                  <HUText fontStyle="LM" color={theme.mainWhite}>
                    ·
                  </HUText>
                  <HUText fontStyle="BS" color={theme.neutralColor600}>
                    {item.getWordingPause()}
                  </HUText>
                </>
              )}
            </div>
            <div className="flex align-items-center gap-1 pl-3">
              <HUText fontStyle="LM" color={theme.mainWhite}>
                {word("box.member.table.offer.item.start")}
              </HUText>
              <HUText fontStyle="LM" color={theme.mainWhite}>
                ·
              </HUText>
              <HUText fontStyle="BS" color={theme.neutralColor600}>
                {item instanceof BoxSubscriptionDetailsEntity && item.deferredDate
                  ? dayjs(item.deferredDate).format("L")
                  : dayjs(item.creationDate).format("L")}
              </HUText>
            </div>

            {item.status === BoxSubscriptionStatus.INACTIVE ? (
              <div className="flex align-items-center gap-1 pl-3">
                <HUText fontStyle="LM" color={theme.mainWhite}>
                  {word("box.member.table.offer.item.end")}
                </HUText>
                <HUText fontStyle="LM" color={theme.mainWhite}>
                  ·
                </HUText>
                <HUText fontStyle="BS" color={theme.neutralColor600}>
                  {dayjs(item.subscriptionEndDate).format("L")}
                </HUText>
              </div>
            ) : (
              <div className="flex align-items-center gap-1 pl-3">
                <HUText fontStyle="LM" color={theme.mainWhite}>
                  {item.offer.type === BoxOfferType.Subscription || item.offer.type === BoxOfferType.Millenium
                    ? word("box.member.table.offer.item.commitmentPeriod")
                    : word("box.member.table.offer.item.validityPeriod")}
                </HUText>
                <HUText fontStyle="LM" color={theme.mainWhite}>
                  ·
                </HUText>
                <HUText fontStyle="BS" color={theme.neutralColor600}>
                  {capitalize(getOfferCommitment(item.offer))}
                </HUText>
              </div>
            )}
          </div>
        </div>
        {/* PRICE */}
        <div className="flex flex-column gap-2 w-full">
          <div className="flex w-full gap-2 align-items-baseline">
            <HUText fontStyle="BS" color={theme.neutralColor600} className="font-semibold">
              {word("box.member.table.offer.item.price")}
            </HUText>
            <div className="flex flex-grow-1 border-top-1 border-neutralColor-200" />
          </div>
          <div className="flex w-full gap-1 align-items-center">
            {item.priceObject ? (
              item.status === BoxSubscriptionStatus.SCHEDULED_ACTIVATION && item.referencePrice ? (
                <HUText fontStyle="LL" color={theme.mainWhite}>
                  {formatCurrency(item.referencePrice.value, item.referencePrice.currency, language)}
                </HUText>
              ) : (
                <>
                  <HUText fontStyle="LL" color={theme.mainWhite}>
                    {formatCurrency(item.priceObject.value, item.priceObject.currency, language)}
                  </HUText>
                  {item.referencePrice && item.referencePrice.value !== item.priceObject.value ? (
                    <HUText fontStyle="LM" color={theme.neutralColor400} className="line-through">
                      {formatCurrency(item.referencePrice.value, item.referencePrice.currency, language)}
                    </HUText>
                  ) : null}
                </>
              )
            ) : (
              <HUText fontStyle="LL" color={theme.mainWhite}>
                {word("box.member.table.offer.item.freePrice")}
              </HUText>
            )}
          </div>

          <div className="flex flex-column w-full gap-3">
            {/* PROMO CODE */}
            {item.usedPromoCode && item.priceObject ? (
              <div className="flex flex-column w-full gap-2">
                <div className="flex gap-1 pl-3 align-items-center">
                  <Percent size={15} color={theme.neutralColor} />
                  <HUText fontStyle="LS" color={theme.neutralColor}>
                    {word("box.member.table.offer.item.codePromo")}
                  </HUText>
                </div>
                <div className="flex flex-column gap-1 pl-3">
                  <HUText fontStyle="LM" color={theme.neutralColor}>
                    {item.usedPromoCode.code} -{" "}
                    {getFormattedCouponReduction(
                      item.usedPromoCode.type,
                      item.usedPromoCode.value,
                      language,
                      item.priceObject.currency,
                    )}
                  </HUText>
                </div>
              </div>
            ) : null}
            {/* DISCOUNT */}
            {item.coupon && item.priceObject ? (
              <div className="flex flex-column w-full gap-2">
                <div className="flex gap-1 pl-3 align-items-center">
                  <Tag size={15} color={theme.neutralColor} />
                  <HUText fontStyle="LS" color={theme.neutralColor} className="font-semibold">
                    {word("box.member.table.offer.item.discount.label")}
                  </HUText>
                </div>
                <div className="flex flex-column gap-1 pl-3">
                  <HUText fontStyle="BS" color={theme.mainWhite}>
                    {getFormattedCouponReduction(
                      item.coupon.type,
                      item.coupon.value,
                      language,
                      item.priceObject.currency,
                    )}{" "}
                    {item.coupon.type !== CouponType.PERCENT &&
                      `
                       - ${item.getWordingCouponPriceAfterDiscount(language)}`}
                  </HUText>
                  <HUText fontStyle="LS" color={theme.neutralColor600}>
                    {item.getWordingCouponDate()}
                  </HUText>
                </div>
              </div>
            ) : null}
          </div>
        </div>
        {menuActions ? (
          <div className="flex flex-row-reverse w-full" ref={ref}>
            {menuActions}
          </div>
        ) : null}
      </motion.div>
    </div>
  )
}
